import React, { useState } from 'react';
import AppHeader from '../components/AppHeader';
import { useHistory } from '../state/HistoryContext';
import { ClockIcon, TrashIcon } from '../components/icons';
import './History.css';

// Standalone destination reached from Home — meals the user has already made.
// "Cook again" is the way back into the flow: it hands Home the same meal so
// it resolves into a fresh recipe.

function formatDate(ts) {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function monthLabel(ts) {
  return new Date(ts).toLocaleString('default', { month: 'long', year: 'numeric' });
}

function groupByMonth(entries) {
  const groups = [];
  let currentLabel = null;
  for (const entry of entries) {
    const label = monthLabel(entry.completedAt);
    if (label !== currentLabel) {
      groups.push({ label, entries: [] });
      currentLabel = label;
    }
    groups[groups.length - 1].entries.push(entry);
  }
  return groups;
}

export default function History({ onBack, onCookAgain }) {
  const { entries, hasMore, viewAll, removeEntry, clearAll, totalCount } = useHistory();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  const groups = groupByMonth(entries);

  return (
    <div className="screen">
      <div className="screen__inner">
        <AppHeader onBack={onBack} />
        <h1 className="screen__title">History</h1>

        {totalCount === 0 ? (
          <section className="card history-empty">
            <p className="card__label">No meals yet</p>
            <p className="history-empty__body">Meals you&rsquo;ve made will show up here.</p>
            <button type="button" className="button button--primary" onClick={onBack}>
              Make something
            </button>
          </section>
        ) : (
          <>
            {groups.map((group) => (
              <section key={group.label} className="history-group">
                <p className="history-group__label">{group.label}</p>

                {group.entries.map((entry) => (
                  <div key={entry.id} className="card history-entry">
                    <p className="history-entry__name">{entry.mealName}</p>
                    <p className="meta-row history-entry__meta">
                      <ClockIcon size={13} /> {formatDate(entry.completedAt)}
                      {entry.store ? ` · ${entry.store}` : ''}
                    </p>

                    <div className="history-entry__actions">
                      <button
                        type="button"
                        className="button button--secondary history-entry__cook-again"
                        onClick={() => onCookAgain(entry)}
                      >
                        Cook again
                      </button>

                      {confirmDeleteId === entry.id ? (
                        <span className="history-entry__confirm">
                          Delete?
                          <button
                            type="button"
                            onClick={() => {
                              removeEntry(entry.id);
                              setConfirmDeleteId(null);
                            }}
                          >
                            Yes
                          </button>
                          <button type="button" onClick={() => setConfirmDeleteId(null)}>
                            No
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="history-entry__delete"
                          aria-label="Delete entry"
                          onClick={() => setConfirmDeleteId(entry.id)}
                        >
                          <TrashIcon size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </section>
            ))}

            {hasMore && (
              <button type="button" className="link-button" onClick={viewAll}>
                View all history
              </button>
            )}

            {confirmClearAll ? (
              <p className="history-clear-confirm">
                Clear all history? This can&rsquo;t be undone.
                <br />
                <button
                  type="button"
                  onClick={() => {
                    clearAll();
                    setConfirmClearAll(false);
                  }}
                >
                  Yes, clear all
                </button>
                <button type="button" onClick={() => setConfirmClearAll(false)}>
                  Cancel
                </button>
              </p>
            ) : (
              <button
                type="button"
                className="link-button history-clear"
                onClick={() => setConfirmClearAll(true)}
              >
                Clear all history
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
